import { useCallback, useEffect, useRef, useState } from 'react'

export interface Async<T> {
  data: T | undefined
  error: unknown
  loading: boolean
  reload: () => void
}

/** Load data for a key; ignores responses that arrive after the key changed. */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): Async<T> {
  const [state, setState] = useState<{ data?: T; error?: unknown; loading: boolean }>({ loading: true })
  const [tick, setTick] = useState(0)
  const seq = useRef(0)
  useEffect(() => {
    const mine = ++seq.current
    setState((s) => ({ data: s.data, loading: true }))
    load().then(
      (data) => mine === seq.current && setState({ data, loading: false }),
      (error) => mine === seq.current && setState({ error, loading: false }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])
  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data: state.data, error: state.error, loading: state.loading, reload }
}

/** Re-run `reload` every `ms` while the page is visible (live views such as incidents). */
export function usePolling(reload: () => void, ms: number) {
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') reload()
    }, ms)
    const onVisible = () => document.visibilityState === 'visible' && reload()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(t)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [reload, ms])
}

export type ThemePref = 'system' | 'light' | 'dark'

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem('fnms-theme')
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

/** Theme preference (per browser) and the effective theme the map should render. */
export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(readPref)
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false)
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq?.addEventListener('change', on)
    return () => mq?.removeEventListener('change', on)
  }, [])
  useEffect(() => {
    const root = document.documentElement
    if (pref === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', pref)
    try {
      localStorage.setItem('fnms-theme', pref)
    } catch {
      /* private mode: keep the in-memory preference */
    }
  }, [pref])
  const effective: 'light' | 'dark' = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref
  return { pref, setPref, effective }
}

/** True below the Tailwind `md` breakpoint (panel becomes a bottom sheet). */
export function useNarrow() {
  const q = '(max-width: 767px)'
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(q).matches ?? false)
  useEffect(() => {
    const mq = window.matchMedia?.(q)
    const on = (e: MediaQueryListEvent) => setNarrow(e.matches)
    mq?.addEventListener('change', on)
    return () => mq?.removeEventListener('change', on)
  }, [])
  return narrow
}
